import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowLeft, ArrowRight, Check, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { QUESTIONS } from "@/data/recoveryBuilder";
import RecoveryResults from "@/components/recovery/RecoveryResults";

const Steam = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden z-0">
    {Array.from({ length: 7 }).map((_, i) => (
      <div key={i} className="smoke"
        style={{ left: `${-5 + i * 16}%`, bottom: `${-25 + (i % 3) * 14}%`,
          animationDelay: `${i * 1.9}s`, animationDuration: `${16 + (i % 4) * 3}s` }} />
    ))}
  </div>
);

const ImageCard = ({ o, active, onPick }) => (
  <motion.button whileHover={{ y: -5 }} whileTap={{ scale: 0.98 }}
    data-testid={`builder-opt-${o.value}`} onClick={() => onPick(o.value)}
    className={`group relative text-left overflow-hidden rounded-xl border-2 transition-all duration-300
      ${active ? "border-gold shadow-[0_0_0_4px_rgba(212,168,67,0.18),0_18px_40px_-12px_rgba(0,0,0,0.7)]" : "border-white/10 hover:border-gold/50"}`}>
    <div className="aspect-[4/3] overflow-hidden">
      <img src={o.image} alt={o.label} loading="lazy"
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-espresso via-espresso/30 to-transparent" />
    {active && (
      <div className="absolute top-3 right-3 w-7 h-7 rounded-full bg-gold flex items-center justify-center">
        <Check size={16} className="text-espresso" />
      </div>
    )}
    <div className="absolute bottom-0 left-0 right-0 p-4">
      <h3 className="font-serif text-lg text-cream leading-tight">{o.label}</h3>
      {o.sub && <p className="text-cream/70 text-xs mt-1">{o.sub}</p>}
    </div>
  </motion.button>
);

const IconCard = ({ o, active, onPick }) => {
  const Icon = Icons[o.icon] || Icons.Circle;
  return (
    <motion.button whileHover={{ y: -4 }} whileTap={{ scale: 0.98 }}
      data-testid={`builder-opt-${o.value}`} onClick={() => onPick(o.value)}
      className={`relative text-left p-5 rounded-xl border-2 transition-all duration-300 bg-charcoal/60 backdrop-blur
        ${active ? "border-gold shadow-[0_0_0_4px_rgba(212,168,67,0.18)]" : "border-white/10 hover:border-gold/50"}`}>
      <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-3 transition-colors
        ${active ? "bg-gold text-espresso" : "bg-white/5 text-gold"}`}>
        <Icon size={22} />
      </div>
      <h3 className="font-serif text-lg text-cream leading-tight">{o.label}</h3>
      {o.sub && <p className="text-cream/65 text-xs mt-1">{o.sub}</p>}
      {active && <Check size={18} className="absolute top-4 right-4 text-gold" />}
    </motion.button>
  );
};

export default function Quiz() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const visible = QUESTIONS.filter((s) => !s.show || s.show(answers));
  const current = visible[step];
  const progress = ((step + 1) / visible.length) * 100;
  const selected = current ? answers[current.key] : null;

  const pick = (val) => {
    setAnswers((a) => ({ ...a, [current.key]: val }));
    setTimeout(() => advance(), 280);
  };

  const advance = () => {
    setStep((s) => {
      if (s < visible.length - 1) return s + 1;
      submit();
      return s;
    });
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setLoading(true);
    try {
      const res = await api.recoveryRecommend(answers);
      setResults(res);
    } finally { setLoading(false); }
  };

  if (results) {
    return <RecoveryResults results={results} answers={answers}
      onRestart={() => { setResults(null); setStep(0); setAnswers({}); }} />;
  }

  return (
    <div className="relative min-h-screen bg-espresso pt-16 overflow-hidden" data-testid="recovery-builder">
      <Steam />
      <div className="fixed top-16 left-0 right-0 h-[3px] bg-white/10 z-40">
        <motion.div className="h-full bg-gradient-to-r from-amber to-gold"
          animate={{ width: `${progress}%` }} transition={{ duration: 0.45 }} />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-5 py-12 lg:py-16">
        <div className="text-center mb-2">
          <p className="overline flex items-center justify-center gap-2">
            <Sparkles size={13} /> The Recovery Builder
          </p>
        </div>

        {loading ? (
          <div className="text-center py-32" data-testid="builder-loading">
            <Sparkles className="text-gold mx-auto animate-pulse" size={40} />
            <p className="font-serif text-3xl text-cream mt-5">Designing your recovery setup…</p>
            <p className="text-cream/60 mt-2">Matching saunas, heaters and accessories to your space.</p>
          </div>
        ) : (
          <AnimatePresence mode="wait">
            <motion.div key={step}
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -24 }} transition={{ duration: 0.4 }}>
              <p className="text-gold/80 text-sm font-medium text-center mb-3 tracking-wide">
                Step {step + 1} of {visible.length}
              </p>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-cream text-center max-w-3xl mx-auto leading-tight">
                {current.q}
              </h1>
              {current.hint && (
                <p className="text-cream/55 text-center mt-3 max-w-xl mx-auto">{current.hint}</p>
              )}

              <div className={`mt-10 grid gap-4 ${current.type === "image"
                ? "grid-cols-2 md:grid-cols-3" : "grid-cols-2 md:grid-cols-3"}`}>
                {current.options.map((o) =>
                  current.type === "image"
                    ? <ImageCard key={o.value} o={o} active={selected === o.value} onPick={pick} />
                    : <IconCard key={o.value} o={o} active={selected === o.value} onPick={pick} />)}
              </div>

              <div className="flex items-center justify-between mt-12">
                <button onClick={back} disabled={step === 0} data-testid="builder-back"
                  className="flex items-center gap-2 text-cream/55 hover:text-cream disabled:opacity-25 transition-colors">
                  <ArrowLeft size={16} /> Back
                </button>
                <button onClick={advance} disabled={!selected} data-testid="builder-next"
                  className="flex items-center gap-2 bg-gold text-espresso px-7 py-3 rounded-full font-medium hover:bg-amber disabled:opacity-30 transition-colors">
                  {step === visible.length - 1 ? "Build My Setup" : "Continue"} <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
